/**
 * Veriff embedded-mode gating.
 *
 * Veriff's InContext SDK decides embedded-vs-modal internally using the media
 * query `(max-width: 767px), (hover: none)` (see `checkIfIsVeriffIframeMobile`
 * in `@veriff/incontext-sdk`). When that query matches, Veriff renders its OWN
 * full-screen modal and ignores `embeddedOptions.rootElementID` entirely —
 * leaving any host container we render sitting empty behind the modal.
 *
 * We mirror that exact query so our decisions stay consistent with Veriff's:
 *  - whether to pass `embedded: true` to `createVeriffFrame`
 *  - whether to render the `#veriff-root` container at all
 *
 * Keeping a single source of truth avoids the mismatch the WebSDK shipped with
 * (unconditional `embedded: true` + an always-rendered empty container on
 * mobile — the worst-converting `document_review` path).
 */

/** Veriff's own mobile heuristic (mirrors `checkIfIsVeriffIframeMobile`). */
const VERIFF_MOBILE_MEDIA_QUERY = "(max-width: 767px), (hover: none)";

/**
 * True when Veriff will treat this device as mobile and render its modal
 * (ignoring embedded mode). Best-effort and never throws; mirrors Veriff by
 * treating an unavailable/erroring `matchMedia` as non-mobile.
 */
export function isVeriffMobile(): boolean {
  try {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(VERIFF_MOBILE_MEDIA_QUERY).matches;
  } catch {
    // Mirror Veriff's own fallback: on error, treat as non-mobile so embedded applies.
    return false;
  }
}
